/**
 * Cargo Products management page.
 *
 * A single page combining:
 *   • Product master library (with default wash rate + currency + MSDS PDF).
 *   • Per-customer product rate overrides (multi-currency capable — a single
 *     customer can hold both USD and EUR rates for the same product, ITT-style).
 *
 * Product rate resolution at wash-invoice time (backend `_resolve_unit_price`)
 * uses: per-customer override → product default → legacy category tariff.
 * See `/app/backend/routes/cargo_products.py::resolve_product_rate`.
 */
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Package, Plus, Trash2, FileText, Upload, Download, Loader2,
  ChevronDown, ChevronRight, DollarSign, ExternalLink,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { WashRateCard } from "./Tariffs";

const EDITOR_ROLES = new Set(["admin", "supervisor", "general_manager", "depot_manager"]);

export default function CargoProducts() {
  const { user } = useAuth();
  const canEdit = EDITOR_ROLES.has(user?.role);

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(null);       // product_name currently expanded
  const [editing, setEditing] = useState(null);          // { product_name, ... } — new or existing
  const [customers, setCustomers] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, cRes] = await Promise.all([
        api.get("/cargo-products" + (q.trim() ? `?q=${encodeURIComponent(q.trim())}` : "")),
        api.get("/partners?role=carrier&fields=code,legal_name").catch(() => ({ data: [] })),
      ]);
      const plist = Array.isArray(pRes.data) ? pRes.data : (pRes.data?.items || []);
      setProducts(plist);
      const clist = Array.isArray(cRes.data) ? cRes.data : (cRes.data?.items || []);
      setCustomers(clist.sort((x, y) => x.code.localeCompare(y.code)));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 sm:p-8 max-w-6xl mx-auto space-y-6" data-testid="cargo-products-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500 mb-1">
            Product master
          </div>
          <h1 className="font-display text-3xl font-extrabold flex items-center gap-3">
            <Package className="w-8 h-8 text-blue-700" />
            Cargo products
          </h1>
          <p className="text-sm text-gray-600 mt-2 max-w-2xl">
            Product library with default wash rates and MSDS documents. Feeds wash-order
            pricing, cert paperwork, and hazard reference on the operator flow.
          </p>
        </div>
        {canEdit && (
          <button
            onClick={() => setEditing({ product_name: "", is_food: true })}
            className="ps-btn ps-btn-primary"
            data-testid="new-product-btn"
          >
            <Plus className="w-4 h-4" /> New product
          </button>
        )}
      </header>

      <div className="flex items-center gap-3">
        <input
          className="ps-input flex-1 max-w-md"
          placeholder="Search product or chemical name…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="products-search"
        />
        <span className="text-xs text-gray-500 font-mono">{products.length} products</span>
      </div>

      {/*
        Wash rate card (FOOD/CHEMICAL/WATER tiles + customer category-level
        overrides) — moved off the Tariffs page (2026-09-02) so all wash
        pricing decisions live here. The product-level overrides + MSDS
        surface below fills the gap for multi-currency customers like ITT.
      */}
      <div className="pt-2">
        <WashRateCard canEdit={canEdit} />
      </div>

      <div className="pt-2">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 mb-3">
          Product master
        </div>
      </div>

      {loading ? (
        <div className="text-center text-gray-500 py-10">
          <Loader2 className="w-6 h-6 animate-spin mx-auto" /> Loading…
        </div>
      ) : products.length === 0 ? (
        <div className="border border-dashed border-gray-300 p-10 text-center text-sm text-gray-500">
          No products yet. Click <span className="font-semibold">New product</span> to seed the library.
        </div>
      ) : (
        <div className="border border-gray-200 divide-y">
          {products.map((p) => (
            <ProductRow
              key={p.product_name}
              product={p}
              expanded={expanded === p.product_name}
              onToggle={() => setExpanded(expanded === p.product_name ? null : p.product_name)}
              onEdit={() => setEditing(p)}
              onReload={load}
              canEdit={canEdit}
              customers={customers}
            />
          ))}
        </div>
      )}

      {editing && (
        <ProductEditor
          product={editing}
          isNew={!editing.created_at}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}


function ProductRow({ product, expanded, onToggle, onEdit, onReload, canEdit, customers }) {
  const rate = product.default_wash_rate;
  return (
    <div data-testid={`product-row-${product.product_name.replace(/\s+/g, "_")}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-left"
      >
        {expanded
          ? <ChevronDown className="w-4 h-4 text-gray-400" />
          : <ChevronRight className="w-4 h-4 text-gray-400" />}
        <span className="font-mono font-bold text-sm flex-1">{product.product_name}</span>
        {product.chemical_name && (
          <span className="text-xs text-gray-500 italic">{product.chemical_name}</span>
        )}
        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${
          product.is_food ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"
        }`}>
          {product.is_food ? "Food" : "Chemical"}
        </span>
        {rate?.amount > 0 && (
          <span className="font-mono text-sm text-blue-800 font-bold">
            {rate.currency} {rate.amount.toFixed(2)}
          </span>
        )}
        {product.msds_object_path && (
          <FileText className="w-4 h-4 text-emerald-700" title="MSDS on file" />
        )}
      </button>

      {expanded && (
        <div className="bg-gray-50 border-t border-gray-100 px-6 py-4 space-y-4">
          <div className="grid sm:grid-cols-3 gap-4 text-sm">
            <Kv k="UN number" v={product.un_number || "—"} />
            <Kv k="IMO class" v={product.imo_class || "—"} />
            <Kv k="Default rate" v={rate?.amount > 0 ? `${rate.currency} ${rate.amount.toFixed(2)}` : "—"} />
          </div>
          {product.notes && (
            <div className="text-xs text-gray-600 whitespace-pre-wrap">{product.notes}</div>
          )}

          <MsdsRow product={product} onReload={onReload} canEdit={canEdit} />

          <CustomerRateGrid product={product} customers={customers} canEdit={canEdit} onReload={onReload} />

          {canEdit && (
            <div className="pt-2 flex items-center gap-3 border-t border-gray-200">
              <button onClick={onEdit} className="text-xs text-blue-700 hover:underline">
                Edit product details
              </button>
              <button
                onClick={async () => {
                  if (!window.confirm(`Delete product "${product.product_name}"?\n\nAll customer rate overrides for this product will also be removed.`)) return;
                  try {
                    await api.delete(`/cargo-products/${encodeURIComponent(product.product_name)}`);
                    toast.success("Product deleted");
                    onReload();
                  } catch (e) {
                    toast.error(formatApiError(e));
                  }
                }}
                className="text-xs text-red-700 hover:underline"
                data-testid="delete-product-btn"
              >
                Delete product
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function Kv({ k, v }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">{k}</div>
      <div className="font-mono text-sm text-gray-800 mt-0.5">{v}</div>
    </div>
  );
}


function MsdsRow({ product, onReload, canEdit }) {
  const [busy, setBusy] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [extractResult, setExtractResult] = useState(null);
  const has = !!product.msds_object_path;
  const backend = process.env.REACT_APP_BACKEND_URL;
  const url = `${backend}/api/cargo-products/${encodeURIComponent(product.product_name)}/msds`;

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(
        `/cargo-products/${encodeURIComponent(product.product_name)}/msds`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      toast.success("MSDS uploaded — running auto-extract…");
      onReload();
      // Fire the extractor right away so the user reviews the diff.
      setTimeout(() => runExtract(), 500);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const runExtract = async () => {
    setExtracting(true);
    try {
      const { data } = await api.post(
        `/cargo-products/${encodeURIComponent(product.product_name)}/msds/extract`,
      );
      setExtractResult(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setExtracting(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove MSDS from this product?")) return;
    setBusy(true);
    try {
      await api.delete(`/cargo-products/${encodeURIComponent(product.product_name)}/msds`);
      toast.success("MSDS removed");
      onReload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-gray-200 bg-white px-4 py-3">
      <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-2">MSDS document</div>
      {has ? (
        <div className="flex items-center gap-3 flex-wrap">
          <FileText className="w-5 h-5 text-emerald-700" />
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="text-sm text-blue-700 hover:underline font-mono"
            data-testid="msds-view-link"
          >
            {product.msds_filename || "msds.pdf"} <ExternalLink className="w-3 h-3 inline" />
          </a>
          <span className="text-xs text-gray-500">
            Uploaded {product.msds_uploaded_at?.slice(0, 10)} by {product.msds_uploaded_by}
          </span>
          {canEdit && (
            <>
              <button
                onClick={runExtract}
                disabled={extracting}
                className="text-xs text-emerald-800 hover:underline disabled:opacity-40"
                data-testid="msds-extract-btn"
                title="Ask the LLM to re-read the MSDS and preview auto-fill values"
              >
                {extracting ? "Extracting…" : "Auto-fill from MSDS"}
              </button>
              <button
                onClick={remove}
                disabled={busy}
                className="ml-auto text-xs text-red-700 hover:underline"
                data-testid="msds-delete-btn"
              >
                Remove MSDS
              </button>
            </>
          )}
        </div>
      ) : canEdit ? (
        <label className={`inline-flex items-center gap-2 text-sm ${busy ? "opacity-50" : "cursor-pointer text-blue-700 hover:underline"}`} data-testid="msds-upload-label">
          <Upload className="w-4 h-4" />
          {busy ? "Uploading…" : "Upload MSDS (PDF or image, max 15MB)"}
          <input
            type="file"
            accept="application/pdf,image/*"
            className="hidden"
            disabled={busy}
            onChange={(e) => upload(e.target.files?.[0])}
            data-testid="msds-upload-input"
          />
        </label>
      ) : (
        <div className="text-xs text-gray-500 italic">No MSDS on file.</div>
      )}

      {extractResult && (
        <MsdsExtractModal
          product={product}
          result={extractResult}
          onClose={() => setExtractResult(null)}
          onApplied={() => { setExtractResult(null); onReload(); }}
        />
      )}
    </div>
  );
}


function MsdsExtractModal({ product, result, onClose, onApplied }) {
  // Fields we can auto-apply — must match the backend contract in
  // routes/cargo_products.py::extract_msds_fields.
  const rows = [
    { key: "chemical_name",  label: "Chemical name",   patch: (v) => ({ chemical_name: v }) },
    { key: "un_number",      label: "UN number",       patch: (v) => ({ un_number: v }) },
    { key: "imo_class",      label: "IMO class",       patch: (v) => ({ imo_class: v }) },
    { key: "hazard_class",   label: "Hazard class",    patch: (v) => ({ notes: prependNote(product.notes, `Hazard: ${v}`) }) },
    { key: "flash_point",    label: "Flash point",     patch: (v) => ({ notes: prependNote(product.notes, `Flash point: ${v}`) }) },
    { key: "is_food",        label: "Food-grade",      patch: (v) => ({ is_food: !!v }) },
    { key: "physical_state", label: "Physical state",  patch: (v) => ({ notes: prependNote(product.notes, `State: ${v}`) }) },
    { key: "notes",          label: "Handling notes",  patch: (v) => ({ notes: prependNote(product.notes, v) }) },
  ];
  const extracted = result.extracted || {};
  const [selected, setSelected] = useState(() => {
    // Pre-select every non-null field. Operator un-ticks anything they
    // want to skip (e.g. the notes summary is often verbose).
    const init = {};
    rows.forEach((r) => { init[r.key] = extracted[r.key] != null && extracted[r.key] !== ""; });
    return init;
  });
  const [applying, setApplying] = useState(false);

  const displayValue = (v) => {
    if (v === null || v === undefined || v === "") return <span className="text-gray-400 italic">not found</span>;
    if (typeof v === "boolean") return v ? "Yes (food-grade)" : "No (non-food)";
    return String(v);
  };

  const currentValue = (key) => {
    switch (key) {
      case "chemical_name": return product.chemical_name;
      case "un_number":     return product.un_number;
      case "imo_class":     return product.imo_class;
      case "is_food":       return product.is_food;
      default:              return null;   // hazard/flash/state/notes have no direct field yet
    }
  };

  const apply = async () => {
    if (!result.ok) return;
    const patch = {};
    rows.forEach((r) => {
      const v = extracted[r.key];
      if (selected[r.key] && v !== null && v !== undefined && v !== "") {
        Object.assign(patch, r.patch(v));
      }
    });
    if (Object.keys(patch).length === 0) {
      toast.info("Nothing selected to apply");
      return;
    }
    setApplying(true);
    try {
      await api.patch(`/cargo-products/${encodeURIComponent(product.product_name)}`, patch);
      toast.success("Product updated from MSDS");
      onApplied();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="msds-extract-modal">
      <div className="bg-white border border-gray-300 max-w-3xl w-full shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="font-display text-xl font-bold">Auto-fill from MSDS</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Extracted by {result.model} — review each field, un-tick anything you don't want to apply.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800" data-testid="msds-extract-close">✕</button>
        </div>
        <div className="p-6">
          {!result.ok ? (
            <div className="border border-amber-200 bg-amber-50/50 p-4 text-sm space-y-2">
              <div className="font-bold text-amber-900">Couldn't parse the MSDS response into JSON.</div>
              <div className="text-xs text-amber-800">The LLM returned something we couldn't clean up — usually means the PDF was scanned images, corrupted, or the model refused. Raw first 500 chars:</div>
              <pre className="text-[11px] font-mono bg-white border border-amber-200 p-2 whitespace-pre-wrap max-h-48 overflow-y-auto">{result.raw_snippet || "(empty)"}</pre>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
                  <th className="pb-2 pr-2 text-left w-8">Apply</th>
                  <th className="pb-2 pr-4 text-left">Field</th>
                  <th className="pb-2 pr-4 text-left">Current</th>
                  <th className="pb-2 text-left">MSDS extracted →</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map((r) => {
                  const v = extracted[r.key];
                  const disabled = v === null || v === undefined || v === "";
                  return (
                    <tr key={r.key} className={disabled ? "opacity-50" : ""}>
                      <td className="py-2 pr-2">
                        <input
                          type="checkbox"
                          checked={!!selected[r.key]}
                          disabled={disabled}
                          onChange={(e) => setSelected({ ...selected, [r.key]: e.target.checked })}
                          data-testid={`extract-apply-${r.key}`}
                        />
                      </td>
                      <td className="py-2 pr-4 text-gray-700">{r.label}</td>
                      <td className="py-2 pr-4 text-gray-500 font-mono text-xs">
                        {displayValue(currentValue(r.key))}
                      </td>
                      <td className="py-2 text-blue-800 font-mono text-xs">{displayValue(v)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onClose} className="ps-btn ps-btn-secondary" data-testid="msds-extract-cancel">Cancel</button>
          {result.ok && (
            <button
              onClick={apply}
              disabled={applying}
              className="ps-btn ps-btn-primary"
              data-testid="msds-extract-apply"
            >
              {applying ? <><Loader2 className="w-4 h-4 animate-spin" /> Applying…</> : <>Apply selected</>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


// Small helper — prepend a fact line to the notes field if it isn't
// already there. Keeps the notes column tidy and preserves any manual
// notes the operator typed. Duplicates are safely ignored.
function prependNote(existing, line) {
  if (!line) return existing || null;
  const cleaned = (existing || "").trim();
  if (cleaned.includes(line)) return cleaned;
  return cleaned ? `${line}\n${cleaned}` : line;
}


function CustomerRateGrid({ product, customers, canEdit, onReload }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ customer_code: "", amount: "", currency: "USD" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/product-customer-rates?product_name=${encodeURIComponent(product.product_name)}`);
      setRows(data?.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [product.product_name]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!draft.customer_code || !draft.amount || !draft.currency) {
      toast.error("Pick a customer + enter an amount + currency");
      return;
    }
    try {
      await api.put("/product-customer-rates", {
        customer_code: draft.customer_code,
        product_name: product.product_name,
        amount: parseFloat(draft.amount),
        currency: draft.currency,
      });
      toast.success("Customer rate saved");
      setDraft({ customer_code: "", amount: "", currency: "USD" });
      setAdding(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const del = async (row) => {
    if (!window.confirm(`Remove ${row.customer_code}'s ${row.currency} rate for this product?`)) return;
    try {
      await api.delete(`/product-customer-rates?customer_code=${encodeURIComponent(row.customer_code)}&product_name=${encodeURIComponent(product.product_name)}&currency=${encodeURIComponent(row.currency)}`);
      toast.success("Rate removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="border border-gray-200 bg-white">
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100">
        <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
          Per-customer rate overrides <span className="normal-case font-normal text-gray-400 ml-2">— multi-currency supported (one row per currency)</span>
        </div>
        {canEdit && !adding && (
          <button
            onClick={() => setAdding(true)}
            className="text-xs text-blue-700 hover:underline flex items-center gap-1"
            data-testid="add-customer-rate-btn"
          >
            <Plus className="w-3 h-3" /> Add rate
          </button>
        )}
      </div>

      {loading ? (
        <div className="p-4 text-xs text-gray-500">Loading…</div>
      ) : rows.length === 0 && !adding ? (
        <div className="p-4 text-xs text-gray-500 italic">
          No overrides — every customer pays the default rate for this product.
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-[10px] uppercase tracking-wider text-gray-600">
              <th className="px-4 py-2 text-left">Customer</th>
              <th className="px-4 py-2 text-right">Rate</th>
              <th className="px-4 py-2 text-left">Currency</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {rows.map((r) => (
              <tr key={`${r.customer_code}_${r.currency}`} data-testid={`rate-row-${r.customer_code}-${r.currency}`}>
                <td className="px-4 py-1.5 font-mono">{r.customer_code}</td>
                <td className="px-4 py-1.5 text-right font-mono">{r.amount.toFixed(2)}</td>
                <td className="px-4 py-1.5 font-mono">{r.currency}</td>
                <td className="px-4 py-1.5 text-right">
                  {canEdit && (
                    <button onClick={() => del(r)} className="text-red-700 hover:text-red-900">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {adding && (
              <tr className="bg-yellow-50">
                <td className="px-4 py-1.5">
                  <select
                    value={draft.customer_code}
                    onChange={(e) => setDraft({ ...draft, customer_code: e.target.value })}
                    className="ps-input font-mono text-sm py-1"
                    data-testid="new-rate-customer"
                  >
                    <option value="">— pick —</option>
                    {customers.map((c) => (
                      <option key={c.code} value={c.code}>{c.code}</option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-1.5">
                  <input
                    type="number" step="0.01" min="0"
                    value={draft.amount}
                    onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
                    className="ps-input font-mono text-sm text-right py-1 w-full"
                    data-testid="new-rate-amount"
                  />
                </td>
                <td className="px-4 py-1.5">
                  <select
                    value={draft.currency}
                    onChange={(e) => setDraft({ ...draft, currency: e.target.value })}
                    className="ps-input font-mono text-sm py-1"
                    data-testid="new-rate-currency"
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="GHS">GHS</option>
                  </select>
                </td>
                <td className="px-4 py-1.5 text-right whitespace-nowrap">
                  <button
                    onClick={save}
                    className="text-xs text-blue-700 hover:underline mr-3"
                    data-testid="new-rate-save"
                  >
                    Save
                  </button>
                  <button onClick={() => { setAdding(false); setDraft({ customer_code: "", amount: "", currency: "USD" }); }} className="text-xs text-gray-500 hover:underline">
                    Cancel
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}


function ProductEditor({ product, isNew, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    product_name: product.product_name || "",
    chemical_name: product.chemical_name || "",
    un_number: product.un_number || "",
    imo_class: product.imo_class || "",
    is_food: product.is_food ?? true,
    default_wash_rate: product.default_wash_rate || null,
    notes: product.notes || "",
  }));
  const [saving, setSaving] = useState(false);
  const [stagedMsds, setStagedMsds] = useState(null);   // File | null — attached after Save (new products only)
  const [extracting, setExtracting] = useState(false);

  const setRate = (field, value) => {
    setForm((f) => ({
      ...f,
      default_wash_rate: { ...(f.default_wash_rate || { amount: 0, currency: "USD" }), [field]: value },
    }));
  };

  // On new-product flow, dropping an MSDS runs preview-extract server-side
  // and pre-fills form fields. Nothing is persisted yet — the file itself
  // uploads AFTER the operator confirms Save.
  const handleMsdsPrefill = async (file) => {
    if (!file) return;
    setStagedMsds(file);
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        "/cargo-products/msds/preview-extract",
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      if (!data.ok) {
        toast.warning("Couldn't parse the MSDS — please fill fields manually. Raw response captured in audit log.");
        return;
      }
      const ex = data.extracted || {};
      // Merge extracted values into the form — only overwrite empty fields
      // so we don't clobber anything the operator already typed by hand.
      setForm((f) => ({
        ...f,
        product_name:  f.product_name  || (ex.product_name  || "").toUpperCase(),
        chemical_name: f.chemical_name || ex.chemical_name  || "",
        un_number:     f.un_number     || ex.un_number      || "",
        imo_class:     f.imo_class     || ex.imo_class      || "",
        is_food:       ex.is_food != null ? ex.is_food : f.is_food,
        notes: [ex.hazard_class && `Hazard: ${ex.hazard_class}`,
                ex.flash_point && `Flash point: ${ex.flash_point}`,
                ex.physical_state && `State: ${ex.physical_state}`,
                ex.notes, f.notes].filter(Boolean).join("\n"),
      }));
      toast.success(`Auto-filled from MSDS (${data.model})`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setExtracting(false);
    }
  };

  const save = async () => {
    if (!form.product_name.trim()) { toast.error("Product name is required"); return; }
    setSaving(true);
    try {
      const rate = form.default_wash_rate;
      const cleanRate = rate && rate.amount > 0
        ? { amount: parseFloat(rate.amount), currency: (rate.currency || "USD").toUpperCase() }
        : null;
      const payload = {
        product_name: form.product_name.trim(),
        chemical_name: form.chemical_name || null,
        un_number: form.un_number || null,
        imo_class: form.imo_class || null,
        is_food: !!form.is_food,
        default_wash_rate: cleanRate,
        notes: form.notes || null,
      };
      if (isNew) {
        await api.post("/cargo-products", payload);
        toast.success(`Product '${payload.product_name}' created`);
        // If the operator staged an MSDS during preview-extract, attach it
        // now that the product exists.
        if (stagedMsds) {
          try {
            const fd = new FormData();
            fd.append("file", stagedMsds);
            await api.post(
              `/cargo-products/${encodeURIComponent(payload.product_name)}/msds`,
              fd,
              { headers: { "Content-Type": "multipart/form-data" } },
            );
            toast.success("MSDS attached");
          } catch (e) {
            toast.warning("Product saved but MSDS attach failed — upload it manually.");
          }
        }
      } else {
        const patch = { ...payload };
        delete patch.product_name;
        await api.patch(`/cargo-products/${encodeURIComponent(product.product_name)}`, patch);
        toast.success("Product updated");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="product-editor-modal">
      <div className="bg-white border border-gray-300 max-w-2xl w-full shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="font-display text-xl font-bold">
            {isNew ? "New cargo product" : `Edit ${product.product_name}`}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800" data-testid="product-editor-close">✕</button>
        </div>
        <div className="p-6 space-y-4">
          {isNew && (
            <div className="border border-emerald-200 bg-emerald-50/40 p-3 rounded-sm" data-testid="msds-prefill-block">
              <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-emerald-900 mb-2 flex items-center gap-2">
                <FileText className="w-3.5 h-3.5" /> Have an MSDS? Auto-fill from it
              </div>
              {stagedMsds ? (
                <div className="text-xs text-emerald-800 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  <span className="font-mono">{stagedMsds.name}</span>
                  <span className="text-gray-500">— will attach after Save</span>
                  <button onClick={() => setStagedMsds(null)} className="ml-auto text-xs text-red-700 hover:underline">Remove</button>
                </div>
              ) : (
                <label className={`inline-flex items-center gap-2 text-sm ${extracting ? "opacity-50" : "cursor-pointer text-emerald-800 hover:underline"}`}>
                  <Upload className="w-4 h-4" />
                  {extracting ? "Reading MSDS…" : "Drop MSDS PDF here (fills fields below)"}
                  <input
                    type="file"
                    accept="application/pdf"
                    className="hidden"
                    disabled={extracting}
                    onChange={(e) => handleMsdsPrefill(e.target.files?.[0])}
                    data-testid="new-product-msds-input"
                  />
                </label>
              )}
              <p className="text-[10px] text-gray-500 mt-1.5">
                Reads the PDF with an LLM and populates chemical name, UN, IMO, hazard notes, and category.
                You can still edit anything before saving.
              </p>
            </div>
          )}
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Product name *</label>
            <input
              disabled={!isNew}
              value={form.product_name}
              onChange={(e) => setForm({ ...form, product_name: e.target.value })}
              className="ps-input font-mono"
              placeholder="e.g. PALM OIL"
              data-testid="product-name-input"
            />
            {!isNew && (
              <p className="text-[10px] text-gray-500 mt-1">Renames aren't supported — delete + create if you need to rename.</p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Chemical name</label>
              <input value={form.chemical_name} onChange={(e) => setForm({ ...form, chemical_name: e.target.value })} className="ps-input font-mono text-sm" />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Category</label>
              <select value={form.is_food ? "food" : "chem"} onChange={(e) => setForm({ ...form, is_food: e.target.value === "food" })} className="ps-input font-mono">
                <option value="food">Food-grade</option>
                <option value="chem">Chemical</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">UN number</label>
              <input value={form.un_number} onChange={(e) => setForm({ ...form, un_number: e.target.value })} className="ps-input font-mono text-sm" placeholder="e.g. 1223" />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">IMO class</label>
              <input value={form.imo_class} onChange={(e) => setForm({ ...form, imo_class: e.target.value })} className="ps-input font-mono text-sm" placeholder="e.g. 3" />
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 mb-2 flex items-center gap-2">
              <DollarSign className="w-3.5 h-3.5" /> Default wash rate
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Amount</label>
                <input
                  type="number" step="0.01" min="0"
                  value={form.default_wash_rate?.amount ?? ""}
                  onChange={(e) => setRate("amount", parseFloat(e.target.value || "0"))}
                  className="ps-input font-mono"
                  placeholder="0.00"
                  data-testid="default-rate-amount"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Currency</label>
                <select
                  value={form.default_wash_rate?.currency || "USD"}
                  onChange={(e) => setRate("currency", e.target.value)}
                  className="ps-input font-mono"
                  data-testid="default-rate-currency"
                >
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                  <option value="GHS">GHS</option>
                </select>
              </div>
            </div>
            <p className="text-[10px] text-gray-500 mt-2">
              Depot-wide baseline for washing a tank that carried this product. Per-customer overrides
              can be added after saving.
            </p>
          </div>

          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Notes</label>
            <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="ps-input font-mono text-sm" />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onClose} className="ps-btn ps-btn-secondary" data-testid="product-editor-cancel">Cancel</button>
          <button
            onClick={save}
            disabled={saving}
            className="ps-btn ps-btn-primary"
            data-testid="product-editor-save"
          >
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <>Save</>}
          </button>
        </div>
      </div>
    </div>
  );
}
